import { useContext, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SettingsIcon from '@mui/icons-material/Settings';
import DropDownSingle from '../../../../../../Components/DropDownSingle';
import { Modalv2Context } from '../../../../../../Contexts/Modalv2Context';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import { ConfigContext } from '../../../../../../Contexts/ConfigContext';
import useStyles from '../../../../styles';
import useCommonStyles from '../../../../../../commonStyles';
import {
	AUDIO_VERSION,
	CONTENT_MAPPING,
	EXPB_DOC_CONSTANTS
} from '../../../../../../core/constants';
import PlaylistCard from './PlaylistCard';
import ViewPlaylistModal from './ViewPlaylistModal';
import CreatePlaylistModal from './CreatePlaylistModal';
import { getStatusColor } from '../../../../../../utilities/helpers';
import Divider from '@mui/material/Divider';
import AutoRemapModal from './AutoRemapModal';
import GeneralButton from '../../../../../../Components/GeneralButton';

const { INITIALISED, PROCESSED } = CONTENT_MAPPING.VIDEO_CONTENT_STATUS;
const { BLOCKERS } = EXPB_DOC_CONSTANTS;

/**
 * Component for Mapping Videos From Publisher's YouTube Channel.
 */
const YouTubeVideoMode = ({ keyData }) => {
	const classes = useStyles();
	const commonStyleClasses = useCommonStyles();
	const { updateModalContent } = useContext(Modalv2Context);
	const { rawConfig, updateRawConfig } = useContext(ConfigContext);
	const { categories, allPlaylists, siteRawConfig, expbDoc } = useContext(UnitsContext);
	const syncSiteDisabled = expbDoc && expbDoc.blockers.includes(BLOCKERS.SITE_SYNC);
	const mappedPlaylistIdKeyData = `${keyData}.mappedPlaylistId`;

	const assignedPlaylist = useMemo(() => {
		return allPlaylists
			? allPlaylists.find((playlist) => playlist.playlistId === rawConfig[mappedPlaylistIdKeyData])
			: [];
		
	}, [rawConfig[mappedPlaylistIdKeyData]]);

	const addMappingIdToSection = (value) => {
		let clonedRawConfig = structuredClone(rawConfig);
		updateRawConfig({ ...clonedRawConfig, [mappedPlaylistIdKeyData]: value });
	};

	const processedOwnersPlaylists = useMemo(() => {
		return allPlaylists
			.filter((playlistObj) => playlistObj.processStatus === PROCESSED)
			.map(({ playlistName: label, playlistId: value }) => ({
				label,
				value
			}));
	
	}, [allPlaylists]);

	const videosList =
		assignedPlaylist?.activeAudioVersion === AUDIO_VERSION.UNMUTED
			? assignedPlaylist?.unmutedVideosList
			: assignedPlaylist?.mutedVideosList;

	if (!siteRawConfig.ownerEmail) {
		return (
			<Paper style={{ margin: '2rem', padding: '4rem' }}>
				<Typography
					variant="body1"
					textAlign="center"
					style={{ margin: '10rem auto' }}
					color="primary"
				>
					Owner Email must be added in Site Config for Creating Mapping.
				</Typography>
			</Paper>
		);
	}

	return (
		<>
			<AddCircleOutlineOutlinedIcon
				className={commonStyleClasses.icon}
				color="primary"
				fontSize="large"
				style={{
					position: 'fixed',
					bottom: '8rem',
					right: '6rem',
					zIndex: 2
				}}
				onClick={() => updateModalContent(<CreatePlaylistModal categories={categories} />)}
			/>

			<Box
				sx={{
					margin: '2rem',
					padding: '4rem'
				}}
			>
				{allPlaylists && allPlaylists?.length > 0 ? (
					<>
						{syncSiteDisabled && rawConfig[`${keyData}.autoRemap.enabled`] && (
							<Alert sx={{ width: '100%', margin: '2rem auto' }} severity="warning">
								Site Sync is currently disabled. As a result, videos added through auto-remap will
								not be automatically reflected on the live site.
							</Alert>
						)}
						<Grid
							container
							spacing={2}
							style={{ alignItems: 'center', justifyContent: 'space-between' }}
						>
							<Grid item xs={6}>
								<DropDownSingle
									isObjectEditor={false}
									directValue={assignedPlaylist?.playlistId}
									updateDirectValue={(value) => {
										addMappingIdToSection(value);
									}}
									list={processedOwnersPlaylists}
									label="Playlist Assigned"
									labelStyles={{ width: '100%' }}
									formControlStyles={{ width: '100%' }}
									formControlVariant={'outlined'}
									selectInputStyles={{ margin: '1rem 0' }}
									forceDeselect={true}
								/>
							</Grid>
							{assignedPlaylist?.processStatus && (
								<div className={classes.processStatusDiv}>
									<CircleIcon
										fontSize="2rem"
										color={getStatusColor(assignedPlaylist?.processStatus)}
									/>
									<Typography>Status: {assignedPlaylist?.processStatus}</Typography>
								</div>
							)}

							<Grid item xs={6} style={{ marginTop: '3rem' }} container justifyContent="flex-end">
								<Button
									variant="contained"
									sx={{
										margin: '0 2rem',
										gap: '0.2rem'
									}}
									onClick={() =>
										updateModalContent(
											<ViewPlaylistModal
												playlistName={assignedPlaylist?.playlistName}
												videosList={videosList}
											/>
										)
									}
									disabled={!assignedPlaylist || assignedPlaylist?.processStatus === INITIALISED}
								>
									<VisibilityIcon fontSize="10px" />
									View Playlist
								</Button>
								<GeneralButton
									sx={{
										margin: '0 2rem',
										gap: '0.2rem'
									}}
									name="AUTO_REMAP_PLAYLIST_BUTTON"
									color="primary"
									variant="contained"
									onClick={() =>
										updateModalContent(<AutoRemapModal keyData={`${keyData}.autoRemap`} />)
									}
								>
									<SettingsIcon fontSize="10px" />
									Auto Remap
								</GeneralButton>
							</Grid>
						</Grid>
					</>
				) : (
					<Alert sx={{ width: '30rem', margin: '0 auto' }} severity="info">
						No Current Mapping Exist
					</Alert>
				)}
				<Divider sx={{ marginTop: '3rem', marginBottom: '3rem' }} />
				<Typography
					variant="h4"
					color="black"
					fontWeight="600"
					textAlign={'center'}
					marginBottom="2rem"
				>
					Available Playlists
				</Typography>
				{allPlaylists?.length > 0 ? (
					<>
						<Grid container spacing={1} sx={{ flexWrap: 'wrap' }}>
							{allPlaylists.map((playlist, index) => (
								<Grid item xs={6} key={playlist.playlistId}>
									<PlaylistCard
										playlistDetails={playlist}
										index={index}
										assignedPlaylistId={assignedPlaylist?.playlistId}
									/>
								</Grid>
							))}
						</Grid>
					</>
				) : (
					<Typography variant="body1" textAlign="center" style={{ margin: '10rem auto' }}>
						No playlists available, create new playlist to get started!
					</Typography>
				)}
			</Box>
		</>
	);
};

export default YouTubeVideoMode;
